import React, {
  ReactDOM,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import {
  Button,
  ButtonGroup,
  Card,
  Checkbox,
  FilledInput,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  Box,
  TextareaAutosize,
  Fab,
  IconButton,
} from "@mui/material";
import InventoryTracking from "../../../api/InventoryTracking";
import { AuthContext } from "../../../context/AuthContext";
import { PullRequestContext } from "../../../context/PullRequestContext";
import { Search, Edit, CheckBox, Add, Close } from "@mui/icons-material";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import { TopNavContext } from "../../../context/TopNavContext";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import escapeCharacters from "../../../helpers/escapeCharacters";

import PageContent from "../../../component/PageContent";
import toFixedIfNecessary from "../../../helpers/toFixedIfNecessary";
import { useNavigate, useParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import ListItem from "../../../component/ListItem";
import SelectTruckModal from "../Jobs/JobDetails/Tabs/SelectTruckModal";
import { useTheme } from "@emotion/react";
import { DarkLightModeContext } from "../../../context/DarkLightModeContext";
import SelectJob from "../../../component/Selectors/SelectJob";
import SelectMaterialDialog from "./SelectMaterialDialog";

const CreatePullRequest = () => {
  const { isDarkMode } = useContext(DarkLightModeContext);
  const { jobId } = useParams();
  let navigate = useNavigate();
  const { authToken, userBranch } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const {
    grabFromInventory,
    grabFromBoneyard,
    grabFromCommitted,
    grabCount,
    allQuantitiesValid,
    grabs,
    updateInventoryGrabQuantity,
    removeInventoryGrab,
    updateBoneyardGrabQuantity,
    removeBoneyardGrab,
    updateCommittedGrabQuantity,
    removeCommittedGrab,
    grabFromStaged,
    // setDeliveryLocation,
    setStatus,
    setInstructions,
    setNotes,
    setSelectedJob,
    clearSelectedJob,
    clearGrabs,
    setMultiple,
  } = useContext(PullRequestContext);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);

  // const [deliveryLocations, setDeliveryLocations] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]); // pull options is status
  const [search, setSearch] = useState("");
  const [searchDebounce] = useDebounce(search, 1000);
  const [isEditingJob, setIsEditingJob] = useState(false);
  const [isUnlisted, setIsUnlisted] = useState(false);
  const [jobsiteDeliveryInstructions, setJobsiteDeliveryInstructions] =
    useState(null);

  const [truckTagCrew, setTruckTagCrew] = useState(null);
  const [truckTagTruckNumber, setTruckTagTruckNumber] = useState(null);
  const [truckTagFlashingColor, setTruckTagFlashingColor] = useState(null);
  const [truckTagRoofType1, setTruckTagRoofType1] = useState(null);
  const [truckTagRoofType1Size, setTruckTagRoofType1Size] = useState(null);
  const [truckTagRoofType2, setTruckTagRoofType2] = useState(null);
  const [truckTagRoofType2Size, setTruckTagRoofType2Size] = useState(null);

  const { setPageName } = useContext(TopNavContext);

  const [currentStaged, setCurrentStaged] = useState(null);

  // INVENTORY SEARCH BY CATEGORY ----------------------------
  const [categorySearch, setCategorySearch] = useState("");
  const [categorySearchResults, setCategorySearchResults] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [addMore, setAddMore] = useState(false);

  const handleCategorySearch = async (controller) => {
    try {
      if (!categorySearch) {
        setCategorySearchResults([]);
        return;
      }
      setLoading(true);
      let headers = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };
      if (controller) {
        headers.signal = controller.signal;
      }
      const query = escapeCharacters(categorySearch);
      const response = await InventoryTracking.get(
        `/inventory?search=${query}&location=SDL&pageSize=20&page=${page}`,
        headers,
      );
      const data = {
        items: response.data.materials,
        pages: response.data.pages,
      };
      console.log(data);
      setLoading(false);
      setCategorySearchResults([...data.items]);
      setPages(data.pages);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  const handleClearPullData = () => {
    if (jobId) {
      clearGrabs(true);
    } else {
      clearGrabs(false);
    }
  };

  // ----------------------
  // INFINITE SCROLLING
  const observer = useRef();
  const lastElement = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && page < pages) {
          console.log("visible");
          setPage((curr) => curr + 1);
        }
      });
      if (node) observer.current.observe(node);
      // console.log("NODE: ", node);
    },
    [loading, page < pages],
  );
  //

  // const getLocations = async () => {
  //   try {
  //     const response = await InventoryTracking.get(`/locations`, {
  //       headers: {
  //         Authorization: `Bearer ${authToken}`,
  //       },
  //     });
  //     console.log(response.data);
  //     setLocations(response.data.locations);
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  const getStatusOptions = async () => {
    try {
      const response = await InventoryTracking.get(`/pulls/statuses`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      console.log(response.data);
      setStatusOptions(response.data.statuses);
    } catch (error) {
      console.log(error);
    }
  };
  const getJobDetails = async (id) => {
    try {
      const response = await InventoryTracking.get(`/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      let job = response.data;
      setSelectedJob(job.id, job.job_name, job.projectid);
      setIsUnlisted(false);
      setIsEditingJob(false);
    } catch (error) {
      console.log(error);
    }
  };

  const submitGrabs = async () => {
    try {
      setLoading(true);

      if (grabs.selectedJob) grabs.project_backlog_id = grabs.selectedJob.id;

      // Remove any 0 quantity items from grabs
      let grabsCopy = { ...grabs };
      let invCopy = [];
      let boneyardCopy = [];
      let committedCopy = [];
      let stagedCopy = [];

      for (let item of grabsCopy.inventory) {
        if (item.quantity > 0) {
          invCopy.push(item);
        }
      }
      for (let item of grabsCopy.boneyard) {
        if (item.quantity > 0) {
          boneyardCopy.push(item);
        }
      }
      for (let item of grabsCopy.committed) {
        if (item.quantity > 0) {
          committedCopy.push(item);
        }
      }
      for (let item of grabsCopy.staged) {
        if (item.quantity > 0) {
          stagedCopy.push(item);
        }
      }

      grabsCopy = {
        ...grabsCopy,
        instructions: "Pull & Deliver", // DEFAULT EVERYTHING TO DELIVERED
        jobsite_delivery_instructions: jobsiteDeliveryInstructions,

        truck_tag_crew: truckTagCrew,
        truck_tag_truck_number: truckTagTruckNumber,
        truck_tag_flashing_color: truckTagFlashingColor,
        truck_tag_roof_type_1: truckTagRoofType1,
        truck_tag_roof_type_1_size: truckTagRoofType1Size,
        truck_tag_roof_type_2: truckTagRoofType2,
        truck_tag_roof_type_2_size: truckTagRoofType2Size,

        inventory: invCopy,
        boneyard: boneyardCopy,
        committed: committedCopy,
        staged: stagedCopy,
        // jobsite_delivery_instructions:
        //   grabsCopy.instructions === "Pull & Deliver"
        //     ? jobsiteDeliveryInstructions
        //     : null,
      };

      if (
        grabsCopy.staged.length > 0 &&
        grabsCopy.delivery_location === "YARD"
      ) {
        openSnackMessage(
          "error",
          "You cannot stage items that are already in Staged. Please pickj a different delivery location",
        );
      } else {
        const response = await InventoryTracking.post(
          `/pull-requests`,
          grabsCopy,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          },
        );
        console.log(response.data);
        handleClearPullData();
        setIsUnlisted(false);
        openSnackMessage("success", "Pull Request created");
        navigate(`/pull-requests`);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const grabItem = async (item) => {
    try {
      setLoading(true);
      let itemId;
      itemId = item.inventoryId;

      const response = await InventoryTracking.get(`/inventory/${itemId}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      let material = response.data.material;
      console.log("MATERIAL: ", material);

      // Set Modal Data
      grabFromInventory(material, null);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    setTimeout(() => {
      handleCategorySearch(controller);
      setPage(1);
    }, 300);
    return () => {
      controller.abort(); // abort api call on unmount for cleanup
    };
  }, [categorySearch]);

  useEffect(() => {
    // getLocations();
    getStatusOptions();
  }, []);

  useEffect(() => {
    setPageName("Quick Pull");
  }, []);

  return (
    <PageContent
      style={{
        position: "relative",
      }}
    >
      <h2 style={{ textAlign: "center" }}>Pull Request</h2>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          startIcon={
            <RestartAltIcon
              style={{
                width: "40px",
                height: "40px",
              }}
            />
          }
          variant="text"
          style={{}}
          onClick={() => {
            handleClearPullData();
          }}
        >
          Restart
        </Button>
      </div>

      {/* JOB SECTION */}
      <SelectJob
        value={grabs.selectedJob}
        onUpdate={(job) => {
          if (job) {
            setSelectedJob(job.id, job.job_name, job.projectid);
            setIsUnlisted(false);
            setIsEditingJob(false);
          } else {
            clearSelectedJob();
          }
        }}
      />

      {/* <Box>
        <div
          style={{
            width: "100%",
            marginTop: 16,
          }}
        >
          <FormControl fullWidth>
            <Typography>Jobsite Delivery Instructions (required)</Typography>
            <TextareaAutosize
              minRows={5}
              aria-label="empty textarea"
              placeholder="Add instructions for drop off"
              style={{
                width: "100%",
                marginTop: 4,
                background: isDarkMode
                  ? "#232323"
                  : theme.palette.primary.extraDark,
                color: theme.palette.secondary.contrastText,
                borderColor: theme.palette.primary.main,
              }}
              value={grabs.jobsiteDeliveryInstructions}
              onChange={(e) => {
                setJobsiteDeliveryInstructions(e.target.value);
              }}
            />
          </FormControl>
        </div>
      </Box> */}
      <Box>
        <Box sx={{ marginTop: "16px", display: "flex", width: "100%" }}>
          <TextField
            sx={{ flex: 1 }}
            label="Crew"
            size="small"
            value={truckTagCrew}
            onChange={(e) => {
              setTruckTagCrew(e.target.value);
            }}
          />
          <TextField
            sx={{ flex: 1, marginLeft: "8px" }}
            label="Truck Number"
            size="small"
            value={truckTagTruckNumber}
            onChange={(e) => {
              setTruckTagTruckNumber(e.target.value);
            }}
          />
          <TextField
            sx={{ flex: 1, marginLeft: "8px" }}
            label="Flashing Color"
            size="small"
            value={truckTagFlashingColor}
            onChange={(e) => {
              setTruckTagFlashingColor(e.target.value);
            }}
          />
        </Box>
        <Box sx={{ marginTop: "16px", display: "flex", width: "100%" }}>
          <TextField
            sx={{ flex: 1 }}
            label="Roof Type 1"
            size="small"
            value={truckTagRoofType1}
            onChange={(e) => {
              setTruckTagRoofType1(e.target.value);
            }}
          />
          <TextField
            sx={{ flex: 1, marginLeft: "8px" }}
            label="Roof Type 1 Size (squares)"
            size="small"
            value={truckTagRoofType1Size}
            onChange={(e) => {
              setTruckTagRoofType1Size(e.target.value);
            }}
          />
        </Box>
        <Box sx={{ marginTop: "16px", display: "flex", width: "100%" }}>
          <TextField
            sx={{ flex: 1 }}
            label="Roof Type 2"
            size="small"
            value={truckTagRoofType2}
            onChange={(e) => {
              setTruckTagRoofType2(e.target.value);
            }}
          />
          <TextField
            sx={{ flex: 1, marginLeft: "8px" }}
            label="Roof Type 2 Size (squares)"
            size="small"
            value={truckTagRoofType2Size}
            onChange={(e) => {
              setTruckTagRoofType2Size(e.target.value);
            }}
          />
        </Box>
      </Box>

      <Typography variant="h6" sx={{ marginTop: "20px" }}>
        Items
      </Typography>
      {grabs.inventory.map((grabbed, grabbedIndex) => (
        <Box
          key={grabbedIndex}
          sx={{ display: "flex", flexDirection: "row", marginTop: "16px" }}
        >
          <Typography sx={{ flex: 1 }}>{grabbed.item_description}</Typography>
          <TextField
            label="Qty To Pull"
            size="small"
            type="number"
            value={grabbed.quantity ?? ""}
            onChange={(e) => {
              updateInventoryGrabQuantity(grabbed.material_id, e.target.value);
            }}
            sx={{ width: 100 }}
          />
          <IconButton
            onClick={() => {
              removeInventoryGrab(grabbed.material_id);
            }}
          >
            <Close />
          </IconButton>
        </Box>
      ))}

      <SelectMaterialDialog
        open={addMore}
        onClose={() => {
          setAddMore(false);
        }}
        onCancel={() => {
          setAddMore(false);
        }}
        onSelect={(item) => {
          if (item) {
            grabItem(item);
            setAddMore(false);
          }
        }}
      />
      <div
        style={{
          marginTop: 16,
          marginBottom: 16,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Button
          style={{ width: 200, height: 70, fontSize: 24 }}
          disabled={
            loading ||
            !((grabs.selectedJob || isUnlisted) && grabCount > 0) ||
            !allQuantitiesValid
          }
          variant="contained"
          onClick={() => {
            submitGrabs();
          }}
        >
          Submit
        </Button>
      </div>

      <Fab
        color="primary"
        aria-label="add"
        variant={isSmallScreen ? "circular" : "extended"}
        style={{
          position: "fixed",
          bottom: isSmallScreen ? 24 : 36,
          right: isSmallScreen ? 24 : 36,
          cursor: "pointer",
        }}
        onClick={() => setAddMore(true)}
      >
        <Add /> {isSmallScreen ? "" : "Material"}
      </Fab>
    </PageContent>
  );
};

export default CreatePullRequest;
